import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import {
    ApiRequestService,
    BreadcrumbComponent,
    CookieComponent,
    CookieDialogConfiguration,
    CookieInputs,
    CookieObject,
    CookieService,
    DialogService,
    FooterComponent,
    HeaderComponent,
    IntroductionComponent,
    LoadingOverlayComponent,
    SessionserviceService,
    SiteconfigService,
    TranslatableComponent,
    TranslationService,
    TranslationStore
} from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { Speciality } from './consultation/speciality';
import { AppConfigService } from './core/config/app-config.service';
import { Doctor } from './doctor';
import { GetSiteConfigurationResponse } from './getsiteconfigurationresponse';
import { NavigationMenuItem } from './navigation-menu';
import { SiteConfiguration } from './siteconfiguration';
import { GetTranslationRequest } from './usecases/translation-usecase/read-translation-request';
import { GetTranslationUseCase } from './usecases/translation-usecase/read-translation-usecase';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        HeaderComponent,
        BreadcrumbComponent,
        FooterComponent,
        CommonModule,
        RouterModule,
        RouterOutlet,
        MatIconModule,
        LoadingOverlayComponent
    ]
})
export class AppComponent extends TranslatableComponent {
    address: string | undefined;
    aszfUrl = '';
    private readonly BOOK_APPOINTMENTS_URL = 'https://medicall.cc/idopontfoglalas/regenero/index';
    canonicalUrl = window.location.origin + '/consultation';
    colorOfMobileSidenav = 'lightorange';
    colorOfNavBar = 'grey';
    configuratorUrl = '';
    private readonly COOKIE_CONSENT_KEY = 'cookie_consent';
    cookieValue = '';
    dataManagamentUrl: string | undefined;
    descriptionOfTheSite = 'Magas színvonalú szakrendelő Budapest XIII. kerületében. Foglaljon időpontot online!';
    doctors: Doctor[] = [];
    downloadFileUrl = '';
    email: string | undefined;
    facebookImgUrl = '';
    facebookUrl = 'https://www.facebook.com/profile.php?id=100063541752114';
    getSiteconfigurationUrl = '/websiteconfigurator/get-site-configuration';
    private readonly GOOGLE_MAP_IFRAME_SRC =
        'https://www.google.com/maps/embed/v1/place?key=AIzaSyBSYe-gDScK77G0sZGbV3Ab_FFKizyP_xo&q=Regenero';
    isExtraSmall: Observable<BreakpointState>;
    isRowView = false;
    isSubMenuFullScreen = true;
    landlinePhoneNumber: string | undefined;
    loading = false;

    logoSize = '';
    logoUrl: string | undefined;
    nameOfTheSite = 'Regenero Egészségház';
    navigationMenu: NavigationMenuItem[] = [];
    openingHours: string[] = [];
    phoneNumber: string | undefined;
    production = false;
    regeneroBaseUrl = '';
    simplePayLogo = '';
    specialities?: Speciality[];
    title = 'Regenero Egészségház';
    titleOfSite = 'Regenero Egészségház | Egészségház Budapesten';

    private _appConfigService = inject(AppConfigService);
    private _dialogService = inject(DialogService);

    constructor(
        _translateService: TranslationService,
        private _http: HttpClient,
        private _readTranslationfileUseCase: GetTranslationUseCase,
        public router: Router,
        private _apiRequestService: ApiRequestService,
        @Inject('PRODUCTION') private _production: boolean,
        @Inject('DOWNLOAD_FILE_URL') private _downloadFileUrl: string,
        @Inject('REGENERO_BASE_URL') private _regeneroBaseUrl: string,
        @Inject('CONFIGURATOR_BASE_URL') private _configuratorUrl: string,
        private elem: ElementRef,
        private readonly breakpointObserver: BreakpointObserver,
        private cookieService: CookieService,
        private siteConfigService: SiteconfigService,
        private _sessionService: SessionserviceService
    ) {
        super(_translateService, _http);
        this.production = _production;
        this.downloadFileUrl = _downloadFileUrl;
        this.configuratorUrl = _configuratorUrl;
        this.facebookImgUrl = this.downloadFileUrl + '/facebooklog';
        this.logoSize = '200px';
        this.elem.nativeElement.style.setProperty('--logo-size', this.logoSize);
        this.regeneroBaseUrl = _regeneroBaseUrl;
        this.isExtraSmall = this.breakpointObserver.observe(Breakpoints.XSmall);
    }

    override readTranslations(http: HttpClient, language: string): Observable<TranslationStore> {
        const request: GetTranslationRequest = { url: './assets/i18n/' + language + '.json' };
        this._readTranslationfileUseCase.execute(request) as unknown as Observable<TranslationStore>;
        return this._http.get<TranslationStore>('./assets/i18n/' + language + '.json');
    }

    override onInit(): void {
        this.getSiteConfiguration();
        this.cookieService.addDataLayer();
        this.cookieService.initGoogleConsent();
        const consent = JSON.parse(localStorage.getItem(this.COOKIE_CONSENT_KEY) as string) as CookieObject;
        if (consent && !this.isCookieExpired(consent.expiredTime)) {
            this.cookieService.acceptGoogleConsent(consent);
        } else {
            this.openCookie();
        }
        this.cookieService.loadGtagScripts(this._appConfigService.config.googleTagId);
    }

    isCookieExpired(expiredTime: Date | undefined): boolean {
        if (!expiredTime) {
            return true;
        }
        const expiredDate = new Date(expiredTime);
        const now = new Date();
        return expiredDate < now;
    }

    getParts(sum: number, length: number) {
        const left = Math.ceil(sum / length);
        const right = Math.floor(sum / length);
        const first = (sum - right * length) / right;

        return Array.from({ length }, (_, i) => (i < first ? left : right));
    }

    populate(sum: number, n: number, interval: number): number[] {
        let fill = Math.floor(sum / n);
        if (fill * interval < sum) fill = Math.ceil(sum / interval);
        else if (fill * (interval - 1) > sum) fill = Math.ceil(sum / (sum % interval ? interval - 1 : interval));
        const array = new Array<number>(interval);
        array.fill(fill);
        array[interval - 1] = sum - (interval - 1) * fill;
        return array;
    }

    onOutletLoaded(component: IntroductionComponent) {
        component.isRowView = this.isRowView;
        component.googleMapIframeSrc = this.GOOGLE_MAP_IFRAME_SRC;
        component.bookAppointmentsUrl = this.BOOK_APPOINTMENTS_URL;
    }

    getSiteConfiguration() {
        this._apiRequestService
            .sendRequest<{}, GetSiteConfigurationResponse>(this.configuratorUrl, {}, true, this.getSiteconfigurationUrl)
            .subscribe((response) => {
                const siteConfigurationList = response.siteConfigurationList as SiteConfiguration[];
                const siteConfigurationUrlList = response.siteConfigurationIsUrlList as SiteConfiguration[];

                let isSessionSaveForConfigurationEnded = false;
                let isSessionSaveForConfigurationUrlEnded = false;

                siteConfigurationList.forEach((siteconfig) => {
                    const value = siteconfig.configurationValue;
                    switch (siteconfig.configurationKey) {
                        case 'email':
                            this.email = value;
                            this._sessionService.set('email', value);
                            break;
                        case 'address':
                            this.address = value;
                            this._sessionService.set('address', value);
                            break;
                        case 'cellphone_number':
                            this.phoneNumber = value;
                            this._sessionService.set('cellphone_number', value);
                            break;
                        case 'landline_phone_number':
                            this.landlinePhoneNumber = value;
                            this._sessionService.set('landline_phone_number', value);
                            break;
                        case 'opening_hours':
                            const formatted = this.siteConfigService.formatOpeningHours(value!);
                            this.openingHours = formatted;
                            this._sessionService.set('opening_hours', formatted);
                            break;
                        case 'patient_admisson_form_url':
                            console.log(value);
                            this._sessionService.set('patient_admisson_form_url', value);
                            break;
                    }
                    isSessionSaveForConfigurationEnded = true;
                });

                siteConfigurationUrlList.forEach((siteconfig) => {
                    const value = siteconfig.configurationValue;
                    switch (siteconfig.configurationKey) {
                        case 'mobile_logo_url':
                            sessionStorage.setItem('mobile_logo_url', JSON.stringify(value));
                            break;
                        case 'privacy_policy_url':
                            this.dataManagamentUrl = value;
                            sessionStorage.setItem('privacy_policy_url', JSON.stringify(value));
                            break;
                        case 'logo_url':
                            this.logoUrl = value;
                            sessionStorage.setItem('logo_url', JSON.stringify(value));
                            break;
                        case 'patient_admisson_form_url':
                            this._sessionService.set('patient_admisson_form_url', value);
                            break;
                    }
                    isSessionSaveForConfigurationUrlEnded = true;
                });
                if (isSessionSaveForConfigurationEnded && isSessionSaveForConfigurationUrlEnded) {
                    this._sessionService.setConfigLoaded();
                }
            });
    }

    openCookie() {
        const privacyPolicyUrl = JSON.parse(sessionStorage.getItem('privacy_policy_url') as string) as string;
        const cookiInputs: CookieInputs = {
            hasAnyOtherSettings: false,
            hasGoogleAds: true,
            hasGoogleAnalytics: true,
            backroundColor: '#fcf6e8',
            buttonColor: '#413C3F',
            buttonTextColor: 'white',
            privacyPolicyUrl: privacyPolicyUrl
        };
        const cookieDialogConfiguration: CookieDialogConfiguration = {
            cookieInputs: cookiInputs
        };
        this._dialogService.openDialog(CookieComponent, cookieDialogConfiguration);
    }
}
